import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type {
  StudentGroup,
  CreateGroupData,
  UpdateGroupData,
  AddMemberData,
  GroupFilters,
  PaginatedResponse,
} from '@/types'

interface ApiResponse<T> {
  success?: boolean
  message?: string
  data?: T
}

const BASE_URL = API_ENDPOINTS.GROUPS

export const groupsApi = {
  /**
   * Получить список групп
   */
  getAll: async (filters?: GroupFilters): Promise<PaginatedResponse<StudentGroup>> => {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.search) params.append('search', filters.search)
    if (filters?.page) params.append('page', String(filters.page))
    if (filters?.per_page) params.append('per_page', String(filters.per_page))
    if (filters?.sort_by) params.append('sort_by', filters.sort_by)
    if (filters?.sort_order) params.append('sort_order', filters.sort_order)

    try {
      interface GroupsResponse {
        data?: StudentGroup[]
        meta?: {
          current_page?: number
          last_page?: number
          per_page?: number
          total?: number
        }
        current_page?: number
        last_page?: number
        per_page?: number
        total?: number
        message?: string
      }
      const response = await apiClient.get<StudentGroup[] | GroupsResponse>(`${BASE_URL}?${params.toString()}`)

      // Обрабатываем разные форматы ответа
      if (Array.isArray(response.data)) {
        return {
          data: response.data,
          current_page: 1,
          last_page: 1,
          per_page: 20,
          total: response.data.length,
        }
      }

      const raw = response.data as GroupsResponse
      if (raw?.data && Array.isArray(raw.data)) {
        const meta = raw.meta || {}
        return {
          data: raw.data,
          current_page: meta.current_page ?? raw.current_page ?? 1,
          last_page: meta.last_page ?? raw.last_page ?? 1,
          per_page: meta.per_page ?? raw.per_page ?? 20,
          total: meta.total ?? raw.total ?? 0,
        }
      }

      // Если ответ не в ожидаемом формате, возвращаем пустой список
      console.warn('Unexpected API response format:', response.data)
      return {
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
      }
    } catch (error: unknown) {
      console.error('Error fetching groups:', error)
      const err = error as { response?: { data?: { message?: string } }; message?: string }
      throw new Error(err.response?.data?.message || err.message || 'Ошибка загрузки групп')
    }
  },

  /**
   * Получить группу по ID
   */
  getById: async (id: number): Promise<StudentGroup> => {
    try {
      interface GroupResponse {
        data?: StudentGroup
        id?: number
        message?: string
      }
      const response = await apiClient.get<GroupResponse | StudentGroup>(`${BASE_URL}/${id}`)

      const data = response.data as GroupResponse | StudentGroup
      if (data && 'data' in data && data.data) {
        return data.data
      }

      if (data && 'id' in data && data.id) {
        return data as StudentGroup
      }

      throw new Error((data as GroupResponse)?.message || 'Ошибка загрузки группы')
    } catch (error: unknown) {
      console.error('Error fetching group:', error)
      const err = error as { response?: { data?: { message?: string } }; message?: string }
      throw new Error(err.response?.data?.message || err.message || 'Ошибка загрузки группы')
    }
  },

  /**
   * Создать группу
   */
  create: async (data: CreateGroupData): Promise<StudentGroup> => {
    const response = await apiClient.post<ApiResponse<StudentGroup>>(BASE_URL, data)

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(response.data?.message || 'Ошибка создания группы')
  },

  /**
   * Обновить группу
   */
  update: async (id: number, data: UpdateGroupData): Promise<StudentGroup> => {
    const response = await apiClient.put<ApiResponse<StudentGroup>>(`${BASE_URL}/${id}`, data)

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(response.data?.message || 'Ошибка обновления группы')
  },

  /**
   * Удалить группу
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/${id}`)
  },

  /**
   * Добавить участника в группу
   */
  addMember: async (groupId: number, data: AddMemberData): Promise<StudentGroup> => {
    const response = await apiClient.post<ApiResponse<StudentGroup>>(
      `${BASE_URL}/${groupId}/members`,
      data
    )

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(response.data?.message || 'Ошибка добавления участника')
  },

  /**
   * Удалить участника из группы
   */
  removeMember: async (groupId: number, studentId: number): Promise<StudentGroup> => {
    const response = await apiClient.delete<ApiResponse<StudentGroup>>(
      `${BASE_URL}/${groupId}/members/${studentId}`
    )

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(response.data?.message || 'Ошибка удаления участника')
  },
}
